/* eslint-disable */
import { AnimatePresence, motion } from 'framer-motion';
import { History, Maximize2, MessageSquarePlus, Minimize2, X } from 'lucide-react';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import ChatInput from './ChatInput';
import ChatContextBar from './ChatContextBar';
import ChatMessageList from './ChatMessageList';
import ChatQuickPrompts from './ChatQuickPrompts';
import ChatSessionHistoryPanel from './ChatSessionHistoryPanel';
import ErrorState from './ErrorState';
import Tooltip from '../Common/ToolTip';
import {
  getChatFooterClasses,
  getChatHeaderClasses,
  getChatSurfaceClasses,
  getIconButtonClasses,
} from '../../utils/ai/chatTheme';

const MIN_WIDTH = 380;
const MIN_HEIGHT = 500;
const MAX_WIDTH = 900;
const MAX_HEIGHT = 1000;
const VIEWPORT_MARGIN = 96;
const DEFAULT_WIDTH = 420;
const DEFAULT_HEIGHT = 520;
const EXPANDED_WIDTH = 620;
const EXPANDED_HEIGHT = 640;

const RESIZE_HANDLES = [
  { axis: 'n', className: 'left-[28px] right-[28px] top-0 h-2 cursor-ns-resize' },
  { axis: 's', className: 'bottom-0 left-[28px] right-[28px] h-2 cursor-ns-resize' },
  { axis: 'e', className: 'right-0 top-[28px] bottom-[28px] w-2 cursor-ew-resize' },
  { axis: 'w', className: 'left-0 top-[28px] bottom-[28px] w-2 cursor-ew-resize' },
  { axis: 'nw', className: 'left-0 top-0 h-4 w-4 cursor-nwse-resize' },
  { axis: 'ne', className: 'right-0 top-0 h-4 w-4 cursor-nesw-resize' },
  { axis: 'sw', className: 'left-0 bottom-0 h-4 w-4 cursor-nesw-resize' },
  { axis: 'se', className: 'right-0 bottom-0 h-4 w-4 cursor-nwse-resize' },
];

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const clampPosition = (position, width, height) => {
  const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : width;
  const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : height;
  const maxLeft = Math.max(viewportWidth - width, 0);
  const maxTop = Math.max(viewportHeight - height, 0);

  return {
    left: clamp(position.left, 0, maxLeft),
    top: clamp(position.top, 0, maxTop),
  };
};

const getViewportBounds = () => ({
  maxWidth: typeof window !== 'undefined' ? Math.min(MAX_WIDTH, window.innerWidth - 24) : MAX_WIDTH,
  maxHeight:
    typeof window !== 'undefined'
      ? Math.min(MAX_HEIGHT, Math.max(window.innerHeight - VIEWPORT_MARGIN, MIN_HEIGHT))
      : MAX_HEIGHT,
});

const ChatWindow = (props) => {
  const {
    messages = [],
    loading = false,
    error = null,
    theme = 'dark',
    isExpanded = false,
    isHistoryOpen = false,
    sessions = [],
    activeSessionId = '',
    position = null,
    onPositionChange,
    onClose,
    onToggleExpand,
    onToggleHistory,
    onSelectSession,
    onStartNewSession,
    onSend,
    onPause,
    onRetry,
  } = props;

  const [composerValue, setComposerValue] = useState('');
  const [size, setSize] = useState({ width: DEFAULT_WIDTH, height: DEFAULT_HEIGHT });
  const resizeStateRef = useRef(null);
  const dragStateRef = useRef(null);
  const rafRef = useRef(null);
  const dragRafRef = useRef(null);
  const panelRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    setComposerValue('');
  }, [activeSessionId]);

  useEffect(() => {
    const handleWindowResize = () => {
      if (!position) {
        return;
      }

      const nextPosition = clampPosition(position, size.width, size.height);
      if (nextPosition.left !== position.left || nextPosition.top !== position.top) {
        onPositionChange?.(nextPosition);
      }
    };

    window.addEventListener('resize', handleWindowResize);
    return () => window.removeEventListener('resize', handleWindowResize);
  }, [onPositionChange, position, size.height, size.width]);

  useEffect(() => {
    const preset = isExpanded
      ? { width: EXPANDED_WIDTH, height: EXPANDED_HEIGHT }
      : { width: DEFAULT_WIDTH, height: DEFAULT_HEIGHT };
    setSize(preset);
  }, [isExpanded]);

  useEffect(() => {
    const handleMouseMove = (event) => {
      const resizeState = resizeStateRef.current;
      if (!resizeState) {
        return;
      }

      const { axis, startX, startY, startWidth, startHeight, startLeft, startTop } = resizeState;
      const { maxWidth, maxHeight } = getViewportBounds();
      const deltaX = event.clientX - startX;
      const deltaY = event.clientY - startY;
      const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : maxWidth;
      const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : maxHeight;
      const maxRight = Math.min(viewportWidth, startLeft + maxWidth);
      const maxBottom = Math.min(viewportHeight, startTop + maxHeight);

      let nextWidth = startWidth;
      let nextHeight = startHeight;
      let nextLeft = startLeft;
      let nextTop = startTop;

      if (axis.includes('e')) {
        const desiredRight = clamp(startLeft + startWidth + deltaX, startLeft + MIN_WIDTH, maxRight);
        nextWidth = desiredRight - startLeft;
      }

      if (axis.includes('w')) {
        const desiredLeft = clamp(startLeft + deltaX, Math.max(0, startLeft + startWidth - maxWidth), startLeft + startWidth - MIN_WIDTH);
        nextLeft = desiredLeft;
        nextWidth = startLeft + startWidth - desiredLeft;
      }

      if (axis.includes('s')) {
        const desiredBottom = clamp(startTop + startHeight + deltaY, startTop + MIN_HEIGHT, maxBottom);
        nextHeight = desiredBottom - startTop;
      }

      if (axis.includes('n')) {
        const desiredTop = clamp(startTop + deltaY, Math.max(0, startTop + startHeight - maxHeight), startTop + startHeight - MIN_HEIGHT);
        nextTop = desiredTop;
        nextHeight = startTop + startHeight - desiredTop;
      }

      const nextSize = {
        width: clamp(nextWidth, MIN_WIDTH, maxWidth),
        height: clamp(nextHeight, MIN_HEIGHT, maxHeight),
      };

      const nextPosition = {
        left: clamp(nextLeft, 0, Math.max(viewportWidth - nextSize.width, 0)),
        top: clamp(nextTop, 0, Math.max(viewportHeight - nextSize.height, 0)),
      };

      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }

      rafRef.current = requestAnimationFrame(() => {
        setSize(nextSize);
        if (axis.includes('w') || axis.includes('n')) {
          onPositionChange?.(nextPosition);
        }
      });
    };

    const handleMouseUp = () => {
      resizeStateRef.current = null;
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!position) {
      return undefined;
    }

    const nextPosition = clampPosition(position, size.width, size.height);
    if (nextPosition.left !== position.left || nextPosition.top !== position.top) {
      onPositionChange?.(nextPosition);
    }

    return undefined;
  }, [onPositionChange, position, size.height, size.width]);

  useEffect(() => {
    const handleResize = () => {
      const { maxWidth, maxHeight } = getViewportBounds();
      setSize((current) => ({
        width: clamp(current.width, MIN_WIDTH, maxWidth),
        height: clamp(current.height, MIN_HEIGHT, maxHeight),
      }));
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleMouseMove = (event) => {
      const dragState = dragStateRef.current;
      if (!dragState) {
        return;
      }

      const nextPosition = clampPosition(
        {
          left: event.clientX - dragState.offsetX,
          top: event.clientY - dragState.offsetY,
        },
        dragState.width,
        dragState.height
      );

      if (dragRafRef.current) {
        cancelAnimationFrame(dragRafRef.current);
      }

      dragRafRef.current = requestAnimationFrame(() => {
        onPositionChange?.(nextPosition);
      });
    };

    const handleMouseUp = () => {
      if (!dragStateRef.current) {
        return;
      }

      dragStateRef.current = null;
      setIsDragging(false);

      if (dragRafRef.current) {
        cancelAnimationFrame(dragRafRef.current);
        dragRafRef.current = null;
      }

      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);

      if (dragRafRef.current) {
        cancelAnimationFrame(dragRafRef.current);
        dragRafRef.current = null;
      }

      dragStateRef.current = null;
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }, [onPositionChange]);

  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) {
      return undefined;
    }

    const handleWheel = (event) => {
      if (event.ctrlKey) {
        return;
      }

      event.stopPropagation();

      const historyAside = panel.querySelector('aside');
      const isOverHistory = historyAside && historyAside.contains(event.target);
      const historyScroll = isOverHistory ? historyAside.querySelector('.overflow-y-auto') : null;
      const messageScroll = panel.querySelector('.overflow-y-auto:not(aside *)');
      const scrollTarget = isOverHistory ? (historyScroll || messageScroll) : messageScroll;

      if (!scrollTarget) {
        event.preventDefault();
        return;
      }

      const { scrollTop, scrollHeight, clientHeight } = scrollTarget;
      const isScrollable = scrollHeight > clientHeight;

      if (!isScrollable) {
        event.preventDefault();
        return;
      }

      let delta = event.deltaY;
      if (event.deltaMode === 1) {
        delta *= 24;
      } else if (event.deltaMode === 2) {
        delta *= clientHeight;
      }

      const isOverScrollable = scrollTarget.contains(event.target);

      if (!isOverScrollable) {
        event.preventDefault();
        scrollTarget.scrollTop += delta;
        return;
      }

      const isScrollingUp = delta < 0;
      const isScrollingDown = delta > 0;

      if (isScrollingUp && scrollTop + delta <= 0) {
        scrollTarget.scrollTop = 0;
        event.preventDefault();
      } else if (isScrollingDown && scrollTop + clientHeight + delta >= scrollHeight) {
        scrollTarget.scrollTop = scrollHeight - clientHeight;
        event.preventDefault();
      }
    };

    panel.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      panel.removeEventListener('wheel', handleWheel);
    };
  }, []);

  const getCursorForAxis = (axis) => {
    if (!axis) {
      return 'default';
    }

    if (axis.length === 1) {
      return axis === 'n' || axis === 's' ? 'ns-resize' : 'ew-resize';
    }

    return axis === 'nw' || axis === 'se' ? 'nwse-resize' : 'nesw-resize';
  };

  const startResize = (axis, event) => {
    event.preventDefault();
    event.stopPropagation();
    const rect = panelRef.current?.getBoundingClientRect();
    const currentLeft = position?.left ?? rect?.left ?? 0;
    const currentTop = position?.top ?? rect?.top ?? 0;
    resizeStateRef.current = {
      axis,
      startX: event.clientX,
      startY: event.clientY,
      startWidth: size.width,
      startHeight: size.height,
      startLeft: currentLeft,
      startTop: currentTop,
    };
    document.body.style.userSelect = 'none';
    document.body.style.cursor = getCursorForAxis(axis);
  };

  const startDrag = (event) => {
    if (event.button !== 0) {
      return;
    }

    if (event.target?.closest('button, input, textarea, select, a')) {
      return;
    }

    const rect = panelRef.current?.getBoundingClientRect();
    if (!rect) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    dragStateRef.current = {
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
      width: rect.width,
      height: rect.height,
    };
    setIsDragging(true);

    if (!position) {
      onPositionChange?.({ left: rect.left, top: rect.top });
    }

    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'grabbing';
  };

  const viewportBounds = getViewportBounds();
  const width = clamp(size.width, MIN_WIDTH, viewportBounds.maxWidth);
  const height = clamp(size.height, MIN_HEIGHT, viewportBounds.maxHeight);

  const handleSend = (payload) => {
    const normalized =
      typeof payload === 'string'
        ? { message: payload }
        : payload && typeof payload === 'object'
          ? payload
          : { message: '' };
    const trimmedMessage = String(normalized.message || '').trim();

    onSend?.({ message: trimmedMessage });
    setComposerValue('');
  };

  return (
    <div
      ref={panelRef}
      className={`relative flex flex-col overflow-hidden rounded-[10px] overscroll-contain transition-[background-color,border-color,box-shadow,color,transform] duration-200 ${getChatSurfaceClasses(theme)}`}
      style={{
        width: `${width}px`,
        height: `${height}px`,
        minWidth: `${MIN_WIDTH}px`,
        minHeight: `${MIN_HEIGHT}px`,
        maxWidth: '90vw',
        maxHeight: `calc(100vh - ${VIEWPORT_MARGIN}px)`,
        willChange: 'width, height',
        cursor: resizeStateRef.current ? getCursorForAxis(resizeStateRef.current.axis) : 'default',
        overscrollBehavior: 'contain',
      }}
    >
      <div
        onMouseDown={startDrag}
        className={`relative z-50 flex items-center justify-between px-4 py-3 ${getChatHeaderClasses(theme)} ${
          isDragging ? 'cursor-grabbing' : 'cursor-grab select-none'
        }`}
        style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
      >
        <div className="flex min-w-0 items-center gap-3">
          <span
            className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] ${
              theme === 'dark' ? 'bg-[#066FD1] text-white' : 'bg-[#066FD1] text-white'
            }`}
          >
            <span className="text-sm font-bold">AI</span>
          </span>
          <div className="min-w-0">
            <p className={`text-sm font-semibold ${theme === 'dark' ? 'text-white' : 'text-[#202020]'}`}>
              QMetrix Assistant
            </p>
            <p className={`truncate text-xs ${theme === 'dark' ? 'text-[#A3B1C9]' : 'text-[#6C7A91]'}`}>
              Sprint, release, and dashboard insights
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {onToggleHistory ? (
            <Tooltip content="History" position="bottom">
              <button
                type="button"
                onClick={onToggleHistory}
                className={`inline-flex h-9 w-9 items-center justify-center rounded-md transition ${getIconButtonClasses(theme, { active: isHistoryOpen })}`}
                aria-label="History"
                aria-pressed={isHistoryOpen}
              >
                <History className="h-5 w-5" />
              </button>
            </Tooltip>
          ) : null}
          {onStartNewSession ? (
            <Tooltip content="New Chat" position="bottom">
              <button
                type="button"
                onClick={onStartNewSession}
                className={`inline-flex h-9 w-9 items-center justify-center rounded-md transition ${getIconButtonClasses(theme)}`}
                aria-label="New Chat"
              >
                <MessageSquarePlus className="h-5 w-5" />
              </button>
            </Tooltip>
          ) : null}
          {onToggleExpand ? (
            <Tooltip content={isExpanded ? 'Minimize' : 'Maximize'} position="bottom">
              <button
                type="button"
                onClick={onToggleExpand}
                className={`inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-md transition ${getIconButtonClasses(theme)}`}
                aria-label={isExpanded ? 'Minimize' : 'Maximize'}
              >
                {isExpanded ? <Minimize2 className="h-5 w-5" /> : <Maximize2 className="h-5 w-5" />}
              </button>
            </Tooltip>
          ) : null}
          <Tooltip content="Close" position="bottom">
            <button
              type="button"
              onClick={onClose}
              className={`inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-md transition ${getIconButtonClasses(theme)}`}
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </Tooltip>
        </div>
      </div>

      <div className="pointer-events-none absolute inset-0 z-50">
        {RESIZE_HANDLES.map((handle) => (
          <button
            key={handle.axis}
            type="button"
            onMouseDown={(event) => startResize(handle.axis, event)}
            className={`pointer-events-auto absolute touch-none select-none rounded-[2px] bg-transparent transition-colors hover:bg-[#48A7FF]/10 ${
              handle.axis === 'n' || handle.axis === 's'
                ? 'h-2'
                : handle.axis === 'e' || handle.axis === 'w'
                  ? 'w-2'
                  : 'h-4 w-4'
            } ${handle.className}`}
            aria-label={`Resize ${handle.axis}`}
            title={`Resize ${handle.axis}`}
          />
        ))}
      </div>

      <AnimatePresence initial={false}>
        {isHistoryOpen ? (
          <motion.div
            key="chat-session-history"
            initial={{ x: '-100%', opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: '-100%', opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="absolute bottom-0 left-0 top-[108px] z-40"
          >
            <ChatSessionHistoryPanel
              sessions={sessions}
              activeSessionId={activeSessionId}
              theme={theme}
              onSelectSession={onSelectSession}
              onStartNewSession={onStartNewSession}
            />
          </motion.div>
        ) : null}
      </AnimatePresence>

      <ChatContextBar theme={theme} />

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <ChatMessageList messages={messages} loading={loading} theme={theme} />
      </div>

      <ChatQuickPrompts
        theme={theme}
        disabled={loading}
        onSelect={(prompt) => handleSend(prompt)}
      />

      <div className={`space-y-3 px-4 py-4 ${getChatFooterClasses(theme)}`}>
        {error ? <ErrorState theme={theme} message={error} onRetry={onRetry} /> : null}
        <ChatInput
          onSend={handleSend}
          onPause={onPause}
          isGenerating={loading}
          disabled={loading}
          placeholder="Ask about sprint health, burndown, churn, defects..."
          theme={theme}
          value={composerValue}
          onChange={setComposerValue}
        />
      </div>
    </div>
  );
};

ChatWindow.propTypes = {
  messages: PropTypes.array,
  loading: PropTypes.bool,
  error: PropTypes.string,
  theme: PropTypes.oneOf(['light', 'dark']),
  isExpanded: PropTypes.bool,
  isHistoryOpen: PropTypes.bool,
  sessions: PropTypes.array,
  activeSessionId: PropTypes.string,
  position: PropTypes.shape({
    left: PropTypes.number.isRequired,
    top: PropTypes.number.isRequired,
  }),
  onPositionChange: PropTypes.func,
  onClose: PropTypes.func,
  onToggleExpand: PropTypes.func,
  onToggleHistory: PropTypes.func,
  onSelectSession: PropTypes.func,
  onStartNewSession: PropTypes.func,
  onSend: PropTypes.func,
  onPause: PropTypes.func,
  onRetry: PropTypes.func,
};

export default ChatWindow;
